// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { MouseEvent, useCallback, useState } from "react";

import { mdiContentCopy } from "@mdi/js";
import { Alert, Button, Popover } from "antd";
import styled from "styled-components/macro";

import { Tooltip } from "components/base";

import { HeaderContainer } from "./DataTable.styles";

const COPIED_POPOVER_MS = 2000;

/**
 * The Data Table's cell and column copy, shared with the monthly grid so both behave
 * alike: a cell copies its value, and a column copies its header and every value.
 */
export const useColumnCopy = () => {
  const [copied, setCopied] = useState<Record<string, boolean>>({});

  const copyColumnContents = useCallback(
    (columnName: unknown, rows: Record<string, unknown>[], columnKey: string) => {
      const columnData = rows.map((row) => row[columnKey]);
      const headerAndData = [columnName, ...columnData].join("\n");
      navigator.clipboard
        .writeText(headerAndData)
        .then(() => {
          setCopied((prev) => ({ ...prev, [columnKey]: true }));
          setTimeout(() => {
            setCopied((prev) => ({ ...prev, [columnKey]: false }));
          }, COPIED_POPOVER_MS);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console -- Necessary for debugging copy errors.
          console.error(`Failed to copy. ${err}`);
        });
    },
    []
  );

  // The value goes to the clipboard as given, which coerces it to text.
  const copyCellValue = useCallback((cellValue: unknown) => {
    navigator.clipboard.writeText(cellValue as string).catch((err) => {
      // eslint-disable-next-line no-console -- Necessary for debugging copy errors.
      console.error(`Failed to copy. ${err}`);
    });
  }, []);

  return { copied, copyColumnContents, copyCellValue };
};

interface ColumnCopyButtonProps {
  open: boolean;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}

/** The "Copy Data" button on a column header, with its "Copied" popover. */
export const ColumnCopyButton = ({ open, onClick }: ColumnCopyButtonProps) => (
  <Tooltip title="Copy Data">
    <Popover
      open={open}
      trigger="click"
      overlayClassName="overlay-no-padding"
      content={<Alert type="success" message="Copied to Clipboard!" />}>
      <CopyButton className="dt-copy-button" onClick={onClick}>
        <Icon path={mdiContentCopy} size={1} />
      </CopyButton>
    </Popover>
  </Tooltip>
);

const CopyButton = styled(Button)`
  // Overlay copy icon on data table columns if header width is small.
  position: absolute;
  right: 0; // Place button on the right edge of the container.
  margin-right: 15px;
  background-color: transparent;
  border: none;
  padding: 0;
  opacity: 0;
  transition: opacity 0.2s;

  ${HeaderContainer}:hover & {
    opacity: 1;
    background: none;
  }
`;
